import { FC, useEffect, useRef, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  RadioGroup,
  Radio,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  FormHelperText,
} from 'drift-ui';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './BaseFormStyles.scss';
import {
  binaryValueFormatter,
  useSectionFormHelpers,
  cityRegex,
  companyNameRegex,
  countyRegex,
  dotNumberRegex,
  emailRegex,
  ENTITY_TYPE_OPTIONS,
  mcNumberRegex,
  phoneRegex,
  REFERRAL_OPTIONS,
  taxIdRegex,
  STATE_OPTIONS,
  streetRegex,
  TRAILER_TYPE_OPTIONS,
  zipRegex,
  phoneFormatter,
  numberFormatter,
  currencyFormatter,
  maskTaxId,
  DO_APPLICATION_TYPE,
} from 'client-experience-toolbox';
import { sendDOFirebaseEvent } from 'utils/utils';
import TooltipIcon from '../../assets/images/info.svg';
import { useTranslation } from 'react-i18next';
import { RootState } from 'app/configureStore';
import { AxiosError } from 'axios';
import { DOTCheckModal, DOTCheckState } from '../../components/DotCheckModal';
import { SET_VERIFIED_DOT, SET_DOT_CHECK_RESPONSE } from 'app/actionTypes';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { ContinueButton } from '../../components/ContinueButton';
import { PrefillFMCSAModal } from '../../components/PrefillFMCSAModal';
import { useUpdateApplication } from 'utils/useUpdateApplication';

export const CompanyInformation: FC = () => {
  const application = useSelector((state: RootState) => state?.applicationReducer?.factoringApplication);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isPatching, setIsPatching] = useState(false);
  const branchData = useSelector((state: RootState) => state.applicationReducer.branchData);
  const [prefillTriggerState, setPrefillTriggerState] = useState(Boolean(branchData?.prefillTrigger));
  const features = useSelector((state: RootState) => state?.persistedReducer?.activeFeatures || []);
  const isFMCSAPrefillEnabled = features.includes('ac3e5a0e64775ff22ba4704ed933bd41091f835115637ccb97c9400a07ee0304');
  const { patchApplication, patchFullApplication, getCompanyByDOT, patchApplicationPullData } = useFormNetworkCalls(
    DO_APPLICATION_TYPE.FACTORING,
  );
  const [localTaxId, setLocalTaxId] = useState(application?.applicationData?.company?.irsTaxIdOrEin ?? '');
  const [editingTaxId, setEditingTaxId] = useState(false);
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.FACTORING);

  const {
    textHandlerGenerator,
    selectDropdownHandlerGenerator,
    binaryHandlerGenerator,
    numberHandlerGenerator,
    clearValidation,
    regexValidatorGenerator,
    presenceValidatorGenerator,
    numberValidatorGenerator,
    handleInputFocus,
    onChangeProtectedField,
    onBlurProtectedField,
  } = useSectionFormHelpers({
    errorSetter: setErrors,
    patchApplication,
    updateApplication,
    application,
    fireBaseLogEvent: sendDOFirebaseEvent,
  });
  const applicationData = application?.applicationData;
  const formRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const verifiedDOT = useSelector((state: RootState) => state.applicationReducer.verifiedDOT);
  const [DOTCheckState, setDOTCheckState] = useState<DOTCheckState | undefined>(undefined);
  const [showPrefillModal, setShowPrefillModal] = useState(false);

  const hasLien = applicationData?.company?.lienOnAccounts?.hasLien === true;

  const referralQuestion = () => {
    switch (applicationData?.company?.attributionMethod) {
      case 'Internet':
        return t('what_did_you_search');
      case 'Referral':
        return t('referral_by');
      case 'Social Media':
        return t('what_platform');
      case 'Other':
        return t('another_way');
      default:
        return '';
    }
  };

  const getTaxIdValue = () => {
    if (editingTaxId) {
      return localTaxId || '';
    }
    if (applicationData?.company?.irsTaxIdOrEinDisplay?.length) {
      return maskTaxId(applicationData.company.irsTaxIdOrEinDisplay, true);
    }
    return maskTaxId(localTaxId || '');
  };

  const fieldValidators: { [key: string]: () => boolean } = {
    dotNumber: regexValidatorGenerator({
      key: 'applicationData.company.dotNumber',
      regex: dotNumberRegex,
      errorText: t('invalid_dot'),
      skipPatch: true,
    }),
    companyName: regexValidatorGenerator({
      key: 'applicationData.company.name',
      regex: companyNameRegex,
      errorText: t('invalid_company'),
    }),
    lienHolderName: regexValidatorGenerator({
      key: 'applicationData.company.lienOnAccounts.lienHolderName',
      regex: companyNameRegex,
      errorText: t('invalid_company_name'),
      skip: applicationData?.company?.lienOnAccounts?.hasLien !== true,
    }),
    attributionAdditionalInfo: regexValidatorGenerator({
      key: 'applicationData.company.attributionAdditionalInfo',
      regex: companyNameRegex,
      errorText: t('invalid_referrer'),
      skip: applicationData?.company?.attributionMethod !== 'Referral',
    }),
    doingBusinessAs: regexValidatorGenerator({
      key: 'applicationData.company.doingBusinessAs',
      regex: companyNameRegex,
      errorText: t('invalid_company'),
      optional: true,
    }),
    businessStreetAddress: regexValidatorGenerator({
      key: 'applicationData.company.address.street',
      regex: streetRegex,
      errorText: t('invalid_street'),
    }),
    city: regexValidatorGenerator({
      key: 'applicationData.company.address.city',
      regex: cityRegex,
      errorText: t('invalid_city'),
    }),
    zipCode: regexValidatorGenerator({
      key: 'applicationData.company.address.zip',
      regex: zipRegex,
      errorText: t('invalid_zip'),
    }),
    county: regexValidatorGenerator({
      key: 'applicationData.company.address.county',
      regex: countyRegex,
      errorText: t('invalid_county'),
    }),
    email: regexValidatorGenerator({
      key: 'applicationData.company.email',
      regex: emailRegex,
      errorText: t('invalid_email'),
      makeLowerCase: true,
    }),
    businessPhone: regexValidatorGenerator({
      key: 'applicationData.company.phone',
      regex: phoneRegex,
      errorText: t('invalid_phone'),
    }),
    irsTaxIdOrEin: regexValidatorGenerator({
      key: `applicationData.company.irsTaxIdOrEin`,
      regex: taxIdRegex,
      errorText: t('invalid_tax_id_ein'),
      skip: Boolean(applicationData?.company?.irsTaxIdOrEinDisplay),
      skipPatch: true,
      localValue: localTaxId,
    }),
    mcNumber: regexValidatorGenerator({
      key: 'applicationData.company.mcAuthorityNumber',
      regex: mcNumberRegex,
      errorText: t('invalid_mc_number'),
      optional: true,
    }),
    numberOfCompanyTrucks: numberValidatorGenerator({
      key: 'applicationData.company.numberOfCompanyTrucks',
      range: [0, 10000],
      errorText: t('invalid_truck_amount'),
    }),
    numberOfLeasedTrucks: numberValidatorGenerator({
      key: 'applicationData.company.numberOfLeasedTrucks',
      range: [0, 10000],
      errorText: t('invalid_truck_amount'),
    }),
    // ---- Presence Validations
    state: presenceValidatorGenerator({
      key: 'applicationData.company.address.state',
      errorText: t('invalid_state'),
    }),
    entityType: presenceValidatorGenerator({
      key: 'applicationData.company.entityType',
      errorText: t('invalid_entity_type'),
    }),
    stateFiled: presenceValidatorGenerator({
      key: 'applicationData.company.stateFiled',
      errorText: t('invalid_state'),
    }),
    typeOfTrailer: presenceValidatorGenerator({
      key: 'applicationData.company.typeOfTrailer',
      errorText: t('invalid_type_of_trailer'),
    }),
    estimatedMonthlyVolume: numberValidatorGenerator({
      key: 'applicationData.company.estimatedMonthlyVolume',
      range: [0, 1000000],
      errorText: t('owner_info.error.estimated_monthly_volume'),
    }),
    smallBusinessLoan: presenceValidatorGenerator({
      key: 'applicationData.company.smallBusinessLoan.hasLoan',
      errorText: t('invalid_option'),
    }),
    hasLienOnAccounts: presenceValidatorGenerator({
      key: 'applicationData.company.lienOnAccounts.hasLien',
      errorText: t('invalid_option'),
    }),
    attributionMethod: presenceValidatorGenerator({
      key: 'applicationData.company.attributionMethod',
      errorText: t('invalid_option'),
    }),
  };

  const handleSolePropSelection = async () => {
    const applicants = applicationData?.applicants;
    if (!application?._id || !applicants?.length) return;

    try {
      updateApplication('applicationData.applicants[0].ownershipPercentage', null);
      await patchApplication('applicationData.applicants[0].ownershipPercentage', null);

      const additionalApplicants = applicants.filter(
        (applicant) => applicant.applicantId !== application.primaryApplicantId,
      );

      for (const applicant of additionalApplicants) {
        if (applicant.key) {
          dispatch({
            type: 'REMOVE_ADDITIONAL_OWNER',
            owner: applicant,
            applicationType: DO_APPLICATION_TYPE.FACTORING,
          });
          await patchApplicationPullData(application._id, {
            'applicationData.applicants': { key: applicant.key },
          });
        }
      }
    } catch (error) {
      console.error(`Failed to update application for sole prop: ${error}`);
    }
  };

  const handleEntityTypeChange = (event: SelectChangeEvent) => {
    selectDropdownHandlerGenerator('applicationData.company.entityType', clearValidation)(event);
    if (event.target.value === 'Sole Proprietor') {
      handleSolePropSelection();
    }
  };

  const scrollToError = (selector = '.Mui-error') => {
    const errorInput = formRef.current?.querySelector(selector) as HTMLElement;
    if (errorInput) {
      errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorInput.focus({ preventScroll: true });
    }
  };

  const handleContinueClick = async () => {
    const currentDOT = applicationData?.company?.dotNumber;

    if (currentDOT && currentDOT !== verifiedDOT) {
      setIsPatching(true);
      await patchApplication('applicationData.company.dotNumber', undefined);
      dispatch({ type: SET_VERIFIED_DOT, verifiedDOT: undefined });
      textHandlerGenerator('applicationData.company.dotNumber', clearValidation)('');
      setIsPatching(false);
      return;
    }

    const isValid = await checkFormValidation();

    if (!isValid) {
      scrollToError();
    }
  };

  const checkFormValidation = async () => {
    const results = Object.values(fieldValidators).map((validator, index) => ({
      index,
      isValid: validator(),
    }));
    const firstInvalid = results.find(({ isValid }) => !isValid);

    if (firstInvalid) {
      return false;
    } else {
      await patchFullApplication(setIsPatching);
      return true;
    }
  };

  const focusDotNumberInput = () => {
    const dotInput = formRef.current?.querySelector('#dotNumberInput') as HTMLElement | null;
    if (dotInput) {
      setTimeout(() => dotInput.focus());
    }
  };

  const dotNumberCheck = async (prefill?: boolean) => {
    const isValid = prefill || fieldValidators.dotNumber();
    const dotNumber = prefill ? branchData?.dotNumber : applicationData?.company?.dotNumber;
    if (!dotNumber || dotNumber === verifiedDOT || !isValid) return;

    const dotNumberAction = { type: SET_VERIFIED_DOT, verifiedDOT: dotNumber };
    setDOTCheckState('loading');

    try {
      const applicationType = application?.type?.toUpperCase();
      const response = await getCompanyByDOT(dotNumber, applicationType);

      dispatch({ type: SET_DOT_CHECK_RESPONSE, dotCheckResponse: response });

      await patchApplication('applicationData.company', { dotNumber, name: response.companyName });
      textHandlerGenerator('applicationData.company.name', clearValidation)(response.companyName);
      prefill && textHandlerGenerator(`applicationData.company.dotNumber`, clearValidation)(dotNumber);
      dispatch(dotNumberAction);

      if (response.hasFactoring) setDOTCheckState('found');
      else {
        setDOTCheckState('not_found');
        if (response.prefillData && isFMCSAPrefillEnabled) {
          setShowPrefillModal(true);
        } else {
          console.error('No prefill data available for DOT:', dotNumber);
        }
      }
    } catch (err) {
      console.error('Company not found or error occurred', err);
      if ((err as AxiosError).response?.status === 404) {
        dispatch({ type: SET_DOT_CHECK_RESPONSE, dotCheckResponse: undefined });
        await patchApplication('applicationData.company.dotNumber', dotNumber);
        prefill && textHandlerGenerator(`applicationData.company.dotNumber`, clearValidation)(dotNumber);
        dispatch(dotNumberAction);
        setDOTCheckState('not_found');
      } else {
        setDOTCheckState('error');
        textHandlerGenerator('applicationData.company.dotNumber', clearValidation)('');
      }
    }
  };

  useEffect(() => {
    if (prefillTriggerState) {
      dotNumberCheck(true);
      setPrefillTriggerState(false);
    }
  }, [prefillTriggerState]);

  return (
    <div className={styles.container}>
      <div ref={formRef} className={styles.formFieldContainer}>
        <div>
          <h2 className={styles.title} data-testid="tell-us-about-header">
            {t('tell_us_about')}
          </h2>
        </div>
        <div className={styles.groupedFields}>
          <TextField
            id="dotNumberInput"
            label={t('dot_number')}
            value={applicationData?.company?.dotNumber ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.dotNumber`, clearValidation)}
            onFocus={handleInputFocus}
            onBlur={() => dotNumberCheck()}
            error={!!errors['applicationData.company.dotNumber']}
            helperText={errors['applicationData.company.dotNumber']}
            slotProps={{ htmlInput: { maxLength: 8, inputMode: 'numeric', 'data-testid': 'dot-number-input' } }}
          />
          <TextField
            label={t('company_name')}
            value={applicationData?.company?.name ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.name`, clearValidation)}
            onBlur={fieldValidators.companyName}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.name']}
            error={!!errors['applicationData.company.name']}
            slotProps={{ htmlInput: { maxLength: 50, 'data-testid': 'company-name-input' } }}
          />
        </div>
        <div className={styles.groupedFields}>
          <TextField
            label={t('doing_business_as')}
            value={applicationData?.company?.doingBusinessAs ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.doingBusinessAs`, clearValidation)}
            onBlur={fieldValidators.doingBusinessAs}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.doingBusinessAs'] ?? t('optional')}
            error={!!errors['applicationData.company.doingBusinessAs']}
            slotProps={{
              formHelperText: { sx: { paddingLeft: '12px', color: '#444444', fontSize: '12px' } },
              htmlInput: { maxLength: 50, 'data-testid': 'doing-business-as-input' },
            }}
          />
          <TextField
            label={t('business_street_address')}
            value={applicationData?.company?.address?.street ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.address.street`, clearValidation)}
            onBlur={fieldValidators.businessStreetAddress}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.address.street']}
            error={!!errors['applicationData.company.address.street']}
            slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'business-street-address-input' } }}
          />
        </div>
        <div className={styles.groupedFields}>
          <div className={styles.smallFields}>
            <TextField
              label={t('city')}
              value={applicationData?.company?.address?.city ?? ''}
              onChange={textHandlerGenerator(`applicationData.company.address.city`, clearValidation)}
              onBlur={fieldValidators.city}
              onFocus={handleInputFocus}
              helperText={errors['applicationData.company.address.city']}
              error={!!errors['applicationData.company.address.city']}
              slotProps={{ htmlInput: { maxLength: 50, 'data-testid': 'city-input' } }}
            />
            <FormControl error={!!errors['applicationData.company.address.state']}>
              <InputLabel id="state-label">{t('state')}</InputLabel>
              <Select
                labelId="state-label"
                data-testid="state-dropdown"
                label={t('state')}
                value={applicationData?.company?.address?.state ?? ''}
                onChange={selectDropdownHandlerGenerator(`applicationData.company.address.state`, clearValidation)}
                onFocus={handleInputFocus}
                fullWidth
              >
                {STATE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors[`applicationData.company.address.state`] && (
                <FormHelperText>{errors[`applicationData.company.address.state`]}</FormHelperText>
              )}
            </FormControl>
          </div>
          <div className={styles.smallFields}>
            <TextField
              label={t('zip_code')}
              value={applicationData?.company?.address?.zip ?? ''}
              onChange={textHandlerGenerator(`applicationData.company.address.zip`, clearValidation)}
              onBlur={fieldValidators.zipCode}
              onFocus={handleInputFocus}
              helperText={errors['applicationData.company.address.zip']}
              error={!!errors['applicationData.company.address.zip']}
              slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric', 'data-testid': 'zip-code-input' } }}
            />
            <TextField
              label={t('local_county')}
              value={applicationData?.company?.address?.county ?? ''}
              onChange={textHandlerGenerator(`applicationData.company.address.county`, clearValidation)}
              onBlur={fieldValidators.county}
              onFocus={handleInputFocus}
              helperText={errors['applicationData.company.address.county']}
              error={!!errors['applicationData.company.address.county']}
              slotProps={{ htmlInput: { maxLength: 50, 'data-testid': 'local-county-input' } }}
            />
          </div>
        </div>
        <div className={styles.groupedFields}>
          <TextField
            label={t('email_address')}
            value={applicationData?.company?.email ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.email`, clearValidation, true)}
            onBlur={fieldValidators.email}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.email']}
            error={!!errors['applicationData.company.email']}
            slotProps={{ htmlInput: { autoCapitalize: 'none', 'data-testid': 'email-address-input' } }}
          />
          <TextField
            label={t('business_phone_number')}
            value={phoneFormatter(applicationData?.company?.phone ?? '')}
            onChange={numberHandlerGenerator(`applicationData.company.phone`, clearValidation)}
            onBlur={fieldValidators.businessPhone}
            onFocus={handleInputFocus}
            helperText={errors['applicationData.company.phone']}
            error={!!errors['applicationData.company.phone']}
            slotProps={{
              htmlInput: { maxLength: 16, inputMode: 'numeric', 'data-testid': 'business-phone-number-input' },
            }}
          />
        </div>
        <div className={styles.groupedFields}>
          <div className={styles.tooltipField}>
            <TextField
              label={t('irs_tax_or_ein')}
              onFocus={handleInputFocus}
              value={getTaxIdValue()}
              onChange={(event) =>
                onChangeProtectedField(
                  event,
                  'applicationData.company.irsTaxIdOrEin',
                  setLocalTaxId,
                  setEditingTaxId,
                  editingTaxId,
                )
              }
              onBlur={() =>
                onBlurProtectedField(
                  'applicationData.company.irsTaxIdOrEin',
                  localTaxId,
                  fieldValidators.irsTaxIdOrEin,
                  setEditingTaxId,
                )
              }
              helperText={errors['applicationData.company.irsTaxIdOrEin']}
              error={!!errors['applicationData.company.irsTaxIdOrEin']}
              slotProps={{ htmlInput: { maxLength: 9, inputMode: 'numeric', 'data-testid': 'irs-tax-or-ein-input' } }}
            />
            <div className={styles.tooltipContainer}>
              <Tooltip
                title={t('employee_identification_number')}
                placement="top"
                enterTouchDelay={0}
                leaveTouchDelay={8000}
                slotProps={{
                  tooltip: {
                    className: styles.tooltip,
                  },
                }}
              >
                <img
                  src={TooltipIcon}
                  alt="Information Icon"
                  width={24}
                  height={24}
                  className={styles.tooltipIcon}
                  tabIndex={0}
                />
              </Tooltip>
            </div>
          </div>
          <TextField
            label={t('mc_authority_number')}
            value={applicationData?.company?.mcAuthorityNumber ?? ''}
            onChange={textHandlerGenerator(`applicationData.company.mcAuthorityNumber`, clearValidation)}
            onBlur={fieldValidators.mcNumber}
            onFocus={handleInputFocus}
            error={!!errors['applicationData.company.mcAuthorityNumber']}
            helperText={errors['applicationData.company.mcAuthorityNumber'] || t('optional')}
            slotProps={{
              formHelperText: { sx: { paddingLeft: '12px', color: '#444444', fontSize: '12px' } },
              htmlInput: { maxLength: 7, inputMode: 'numeric', 'data-testid': 'mc-authority-number-input' },
            }}
          />
        </div>
        <div className={styles.groupedFields}>
          <FormControl fullWidth error={!!errors['applicationData.company.entityType']}>
            <InputLabel id="entity-type-label">{t('entity_type')}</InputLabel>
            <Select
              labelId="entity-type-label"
              data-testid="entity-type-dropdown"
              label={t('entity_type')}
              value={applicationData?.company?.entityType ?? ''}
              onChange={handleEntityTypeChange}
              onFocus={handleInputFocus}
            >
              {ENTITY_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Select>
            {errors[`applicationData.company.entityType`] && (
              <FormHelperText>{errors[`applicationData.company.entityType`]}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth error={!!errors['applicationData.company.stateFiled']}>
            <InputLabel id="state-filed-label">{t('states_filed')}</InputLabel>
            <Select
              labelId="state-filed-label"
              data-testid="state-filed-dropdown"
              label={t('states_filed')}
              value={applicationData?.company?.stateFiled ?? ''}
              onChange={selectDropdownHandlerGenerator(`applicationData.company.stateFiled`, clearValidation)}
              onFocus={handleInputFocus}
            >
              {STATE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors[`applicationData.company.stateFiled`] && (
              <FormHelperText>{errors[`applicationData.company.stateFiled`]}</FormHelperText>
            )}
          </FormControl>
        </div>
        <div className={styles.groupedFields}>
          <TextField
            label={t('number_of_company_owned_trucks')}
            value={numberFormatter(applicationData?.company?.numberOfCompanyTrucks?.toString() ?? '')}
            onChange={numberHandlerGenerator(`applicationData.company.numberOfCompanyTrucks`, clearValidation)}
            onBlur={fieldValidators.numberOfCompanyTrucks}
            onFocus={handleInputFocus}
            error={!!errors['applicationData.company.numberOfCompanyTrucks']}
            helperText={errors['applicationData.company.numberOfCompanyTrucks']}
            slotProps={{
              htmlInput: { maxLength: 6, inputMode: 'numeric', 'data-testid': 'number-of-company-owned-trucks-input' },
            }}
          />
          <TextField
            label={t('number_of_company_leased_trucks')}
            value={numberFormatter(applicationData?.company?.numberOfLeasedTrucks?.toString() ?? '')}
            onChange={numberHandlerGenerator(`applicationData.company.numberOfLeasedTrucks`, clearValidation)}
            onBlur={fieldValidators.numberOfLeasedTrucks}
            onFocus={handleInputFocus}
            error={!!errors['applicationData.company.numberOfLeasedTrucks']}
            helperText={errors['applicationData.company.numberOfLeasedTrucks']}
            slotProps={{
              htmlInput: { maxLength: 6, inputMode: 'numeric', 'data-testid': 'number-of-company-leased-trucks-input' },
            }}
          />
        </div>
        <div className={styles.groupedFields}>
          <FormControl fullWidth error={!!errors['applicationData.company.typeOfTrailer']}>
            <InputLabel id="type-of-trailer-label">{t('type_of_trailer')}</InputLabel>
            <Select
              labelId="type-of-trailer-label"
              data-testid="type-of-trailer-dropdown"
              label={t('type_of_trailer')}
              value={applicationData?.company?.typeOfTrailer ?? ''}
              onChange={selectDropdownHandlerGenerator('applicationData.company.typeOfTrailer', clearValidation)}
              onFocus={handleInputFocus}
            >
              {TRAILER_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Select>
            {errors[`applicationData.company.typeOfTrailer`] && (
              <FormHelperText>{errors[`applicationData.company.typeOfTrailer`]}</FormHelperText>
            )}
          </FormControl>
          <TextField
            label={t('estimated_monthly_volume')}
            value={currencyFormatter(applicationData?.company?.estimatedMonthlyVolume?.toString() ?? '')}
            onChange={numberHandlerGenerator(`applicationData.company.estimatedMonthlyVolume`, clearValidation)}
            slotProps={{
              formHelperText: { sx: { paddingLeft: '12px', color: '#666666', fontSize: '14px' } },
              htmlInput: { 'data-testid': 'estimated-monthly-volume-input' },
            }}
            onBlur={fieldValidators.estimatedMonthlyVolume}
            onFocus={handleInputFocus}
            error={!!errors['applicationData.company.estimatedMonthlyVolume']}
            helperText={
              errors['applicationData.company.estimatedMonthlyVolume'] || t('estimated_monthly_volume_helper_text')
            }
          />
        </div>
        <div className={styles.groupedFields}>
          <div className={styles.questionContainer}>
            <p className={styles.questionTitle}>{t('small_business_loan_question')}</p>
            <FormControl error={!!errors['applicationData.company.smallBusinessLoan.hasLoan']}>
              <RadioGroup
                value={binaryValueFormatter(applicationData?.company?.smallBusinessLoan?.hasLoan)}
                onChange={binaryHandlerGenerator('applicationData.company.smallBusinessLoan.hasLoan', clearValidation)}
                onFocus={handleInputFocus}
              >
                <FormControlLabel value="yes" control={<Radio />} label={t('yes')} />
                <FormControlLabel value="no" control={<Radio />} label={t('no')} />
              </RadioGroup>
              {errors['applicationData.company.smallBusinessLoan.hasLoan'] && (
                <FormHelperText>{errors['applicationData.company.smallBusinessLoan.hasLoan']}</FormHelperText>
              )}
            </FormControl>
            <div className={styles.finalQuestionContainer} data-testid="final-question-container">
              <div>
                <p className={styles.optionalQuestionTitle}>{t('fuel_card_question')}</p>
                <p className={styles.optionalText}>{t('optional')}</p>
              </div>
              <FormControl error={!!errors['applicationData.company.hasFuelCard']}>
                <RadioGroup
                  value={binaryValueFormatter(applicationData?.company?.hasFuelCard)}
                  onChange={binaryHandlerGenerator('applicationData.company.hasFuelCard', clearValidation)}
                  onFocus={handleInputFocus}
                >
                  <FormControlLabel value="yes" control={<Radio />} label={t('yes')} />
                  <FormControlLabel value="no" control={<Radio />} label={t('no')} />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
          <div className={styles.questionContainer}>
            <p className={styles.questionTitle}>{t('lien_question')}</p>
            <FormControl error={!!errors['applicationData.company.lienOnAccounts.hasLien']}>
              <RadioGroup
                value={binaryValueFormatter(applicationData?.company?.lienOnAccounts?.hasLien)}
                onChange={binaryHandlerGenerator(`applicationData.company.lienOnAccounts.hasLien`, clearValidation, [
                  'applicationData.company.lienOnAccounts.lienHolderName',
                ])}
                onFocus={handleInputFocus}
              >
                <FormControlLabel value="yes" control={<Radio />} label={t('yes')} />
                <FormControlLabel value="no" control={<Radio />} label={t('no')} />
              </RadioGroup>
              {errors['applicationData.company.lienOnAccounts.hasLien'] && (
                <FormHelperText>{errors['applicationData.company.lienOnAccounts.hasLien']}</FormHelperText>
              )}
            </FormControl>
            {hasLien && (
              <div className={styles.optionalField}>
                <TextField
                  label={t('factor_question')}
                  value={applicationData?.company?.lienOnAccounts?.lienHolderName ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.company.lienOnAccounts.lienHolderName`,
                    clearValidation,
                  )}
                  onFocus={handleInputFocus}
                  onBlur={fieldValidators.lienHolderName}
                  error={!!errors['applicationData.company.lienOnAccounts.lienHolderName']}
                  helperText={errors['applicationData.company.lienOnAccounts.lienHolderName']}
                />
              </div>
            )}
          </div>
        </div>
        <div className={styles.singleField}>
          <FormControl fullWidth error={!!errors['applicationData.company.attributionMethod']}>
            <InputLabel id="how-heard-about-label">{t('how_did_you_hear')}</InputLabel>
            <Select
              labelId="how-heard-about-label"
              data-testid="how-heard-about-dropdown"
              label={t('how_did_you_hear')}
              value={applicationData?.company?.attributionMethod ?? ''}
              onChange={selectDropdownHandlerGenerator('applicationData.company.attributionMethod', clearValidation, [
                'applicationData.company.attributionAdditionalInfo',
              ])}
              onFocus={handleInputFocus}
            >
              {REFERRAL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Select>
            {errors[`applicationData.company.attributionMethod`] && (
              <FormHelperText>{errors[`applicationData.company.attributionMethod`]}</FormHelperText>
            )}
          </FormControl>
        </div>
        {referralQuestion() !== '' && (
          <div className={styles.singleField}>
            <TextField
              label={referralQuestion()}
              value={applicationData?.company?.attributionAdditionalInfo ?? ''}
              onChange={textHandlerGenerator('applicationData.company.attributionAdditionalInfo', clearValidation)}
              onFocus={handleInputFocus}
              onBlur={fieldValidators.attributionAdditionalInfo}
              error={!!errors['applicationData.company.attributionAdditionalInfo']}
              helperText={errors['applicationData.company.attributionAdditionalInfo']}
            />
          </div>
        )}
        <DOTCheckModal
          DOTCheckState={DOTCheckState}
          DOTCompanyInfo={{
            companyName: applicationData?.company?.name,
            dotNumber: applicationData?.company?.dotNumber,
          }}
          updateDotNumber={textHandlerGenerator('applicationData.company.dotNumber', clearValidation)}
          updateCompanyName={textHandlerGenerator('applicationData.company.name', clearValidation)}
          focusDotNumberInput={focusDotNumberInput}
        />
        <PrefillFMCSAModal showModal={showPrefillModal} setShowModal={setShowPrefillModal} />
        <div className={styles.footer}>
          <ContinueButton
            data-testid="company-information-continue-button"
            handleContinueClick={handleContinueClick}
            isPatching={isPatching}
          />
        </div>
      </div>
    </div>
  );
};
